/**
 * /invite/15j — invitation landing page that grants a 15-day trial on signup.
 *
 * Layout :
 *  - Hero gradient (Network-iA signature blue → violet → pink)
 *  - Invitation badge « Vous avez été invité·e »
 *  - 3 selling points
 *  - Inline signup form (email + first name + password)
 *
 * On submit : POST /auth/register with viaInvite=true → trial_expires_at
 * is set server-side to NOW() + 15 days. The TrialBanner then appears
 * automatically in the dashboard.
 *
 * If the user is already logged in → redirect to /. No double-trial.
 */
import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Loader2, Sparkles, Check, ArrowRight, Gift } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { IneaMark } from '@/components/IneaMark';

export default function InvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password || password.length < 8) {
      setError(t('invite.errors.fields') as string);
      return;
    }
    setSubmitting(true);
    try {
      await register(
        email.trim(),
        password,
        displayName.trim() || undefined,
        undefined,
        true, // viaInvite — triggers 15-day trial server-side
      );
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(t('invite.errors.generic') as string);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen w-full"
      style={{
        backgroundImage:
          'radial-gradient(ellipse at 50% -10%, #FFFAF0, #F8F6F0 60%)',
      }}
    >
      <div className="mx-auto flex max-w-[1100px] flex-col gap-8 px-5 py-10 sm:px-8 lg:flex-row lg:gap-12 lg:py-16">
        {/* Hero column */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex-1"
        >
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-pink/30 bg-white/80 px-3 py-1.5 text-[11px] font-700 uppercase tracking-[0.18em] text-pink shadow-sm backdrop-blur">
            <Gift size={12} />
            {t('invite.kicker')}
          </div>

          <h1 className="font-display text-[clamp(28px,4.5vw,46px)] font-800 leading-[1.05] tracking-[-0.025em] text-ink">
            <span className="text-grad">{t('invite.heading.line1')}</span>
            <br />
            {t('invite.heading.line2')}
          </h1>

          <p className="mt-4 max-w-[480px] text-[14.5px] leading-[1.6] text-ink/70">
            {t('invite.subheading')}
          </p>

          <ul className="mt-7 space-y-2.5">
            {[1, 2, 3].map((i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-[13.5px] leading-[1.5] text-ink/80"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet to-pink text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                {t(`invite.benefits.b${i}`)}
              </li>
            ))}
          </ul>

          <p className="mt-6 max-w-[460px] text-[12px] leading-[1.5] text-ink/45">
            {t('invite.afterTrial')}
          </p>
        </motion.section>

        {/* Form column */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex-1 lg:max-w-[440px]"
        >
          <div className="card relative overflow-hidden p-6 sm:p-7">
            <div
              className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-50 blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, rgba(236,72,153,0.3), transparent 70%)',
              }}
            />
            <div
              className="pointer-events-none absolute -bottom-16 -left-12 h-44 w-44 rounded-full opacity-40 blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, rgba(59,130,246,0.25), transparent 70%)',
              }}
            />

            <div className="relative mb-5 flex items-center gap-3">
              <IneaMark size={36} />
              <div>
                <p className="font-mono text-[10px] font-700 uppercase tracking-[0.18em] text-pink">
                  {t('invite.formKicker')}
                </p>
                <h2 className="font-display text-[17px] font-800 tracking-[-0.015em]">
                  {t('invite.formHeading')}
                </h2>
              </div>
            </div>

            <form onSubmit={onSubmit} className="relative space-y-3.5">
              <Field
                label={t('invite.fields.firstName') as string}
                value={displayName}
                onChange={setDisplayName}
                type="text"
                placeholder={t('invite.fields.firstNamePh') as string}
                autoComplete="given-name"
              />
              <Field
                label={t('invite.fields.email') as string}
                value={email}
                onChange={setEmail}
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
              <Field
                label={t('invite.fields.password') as string}
                value={password}
                onChange={setPassword}
                type="password"
                placeholder="••••••••"
                autoComplete="new-password"
                required
              />

              {error && (
                <p className="rounded-lg bg-pink/10 px-3 py-2 text-[12px] font-600 text-pink-deep">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="hero-cta-light-primary inline-flex w-full items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {t('invite.submitting')}
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    {t('invite.submit')}
                    <ArrowRight size={14} />
                  </>
                )}
              </button>

              <p className="text-center text-[11px] text-ink/45">
                {t('invite.alreadyHaveAccount')}{' '}
                <a href="/login" className="font-700 text-violet hover:underline">
                  {t('invite.loginLink')}
                </a>
              </p>
            </form>
          </div>
        </motion.section>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type,
  placeholder,
  autoComplete,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type: 'text' | 'email' | 'password';
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="font-mono text-[10.5px] font-700 uppercase tracking-[0.14em] text-ink/55">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        className="mt-1 block w-full rounded-xl border border-ink/[0.12] bg-white px-3.5 py-2.5 text-[13.5px] text-ink outline-none transition focus:border-pink/60 focus:ring-2 focus:ring-pink/20"
      />
    </label>
  );
}
